import type { TextQuestion } from '@/features/nectar/api/models'
import { completeTextPhase, getTextQuestion, submitTextAnswer } from '@/features/nectar/api/proficiency-assessment'
import { Button, Spinner, Text } from '@/ui-kit'
import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

type PhaseTextQuestionProps = {
  assessmentId: string
  onTextCompleted: () => void
}

export const PhaseTextQuestion = ({ assessmentId, onTextCompleted }: PhaseTextQuestionProps) => {
  const [question, setQuestion] = useState<TextQuestion | null>(null)
  const [questionNum, setQuestionNum] = useState(0)
  const [answeredCount, setAnsweredCount] = useState(0)
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [answering, setAnswering] = useState(false)
  const [loading, setLoading] = useState(true)
  const [completing, setCompleting] = useState(false)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const autoSubmitRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const fetchNextQuestion = useCallback(
    async (id: string) => {
      try {
        const data = await getTextQuestion(id)
        if (data) {
          setQuestion(data)
          setSelectedOption(null)
          setQuestionNum((n) => n + 1)
          clearTimer()
          if (data.timeLimitSec) {
            setTimeLeft(data.timeLimitSec)
            timerRef.current = setInterval(() => {
              setTimeLeft((prev) => {
                if (prev === null || prev <= 1) {
                  clearTimer()
                  return 0
                }
                return prev - 1
              })
            }, 1000)
          } else {
            setTimeLeft(null)
          }
          setLoading(false)
        } else {
          setCompleting(true)
          try {
            await completeTextPhase(id)
            onTextCompleted()
          } catch {
            toast.error('Failed to complete text phase')
          } finally {
            setCompleting(false)
          }
        }
      } catch {
        // 400 = text phase already completed
        onTextCompleted()
      }
    },
    [clearTimer, onTextCompleted]
  )

  const submitAnswer = useCallback(async () => {
    if (!question) return
    clearTimer()
    setTimeLeft(null)
    setAnswering(true)

    try {
      await submitTextAnswer(assessmentId, {
        questionId: question.id,
        selectedOptionIndex: selectedOption
      })
      setAnsweredCount((c) => c + 1)
      await fetchNextQuestion(assessmentId)
    } catch {
      toast.error('Failed to submit answer')
    } finally {
      setAnswering(false)
    }
  }, [assessmentId, question, selectedOption, clearTimer, fetchNextQuestion])

  // Auto-submit when timer expires
  useEffect(() => {
    if (timeLeft === 0 && !answering && !autoSubmitRef.current) {
      autoSubmitRef.current = true
      submitAnswer()
    }
    if (timeLeft !== 0) {
      autoSubmitRef.current = false
    }
  }, [timeLeft, answering, submitAnswer])

  // Initial fetch
  const initialized = useRef(false)
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      fetchNextQuestion(assessmentId)
    }
  }, [assessmentId, fetchNextQuestion])

  // Cleanup timer on unmount
  useEffect(() => () => clearTimer(), [clearTimer])

  if (loading || completing) {
    return (
      <div className="flex w-full justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Spinner />
          <Text type="body2-regular" className="text-gray-80">
            {completing ? 'Computing text results...' : 'Loading question...'}
          </Text>
        </div>
      </div>
    )
  }

  if (!question) return null

  return (
    <div
      className="mx-auto flex w-full max-w-[600px] flex-col gap-5"
      style={{ animation: 'nectar-slide-up 0.3s ease-out' }}
    >
      {/* Header: question number, timer, progress dots */}
      <div className="flex items-center justify-between">
        <Text type="body2-regular" className="text-gray-80">
          Question {questionNum}
        </Text>
        <div className="flex items-center gap-3">
          {timeLeft !== null && (
            <Text
              type="body2-regular"
              className={clsx(
                'font-mono',
                timeLeft <= 5 ? 'text-red' : timeLeft <= 10 ? 'text-orange' : 'text-gray-80'
              )}
            >
              {timeLeft}s
            </Text>
          )}
          <div className="flex gap-1">
            {Array.from({ length: answeredCount }, (_, i) => (
              <div key={i} className="bg-green size-2 rounded-full" />
            ))}
          </div>
        </div>
      </div>

      {/* Question card */}
      <div className="flex flex-col gap-4 rounded-2xl border border-solid border-gray-100 px-6 py-5">
        <div className="flex flex-col gap-1">
          <Text type="body1-regular" className="leading-relaxed">
            {question.prompt}
          </Text>
          <Text type="body3-regular" className="text-gray-80">
            Choose the best answer.{' '}
            {timeLeft !== null ? 'The question will auto-submit when time runs out.' : 'Take your time.'}
          </Text>
        </div>

        <div className="flex flex-col gap-2">
          {question.options.map((opt, i) => (
            <button
              key={opt.text}
              type="button"
              disabled={answering}
              onClick={() => setSelectedOption(i)}
              className={clsx(
                'rounded-xl border border-solid p-3 text-left text-sm transition-colors',
                selectedOption === i ? 'border-green bg-green/5' : 'border-gray-100 hover:bg-gray-100/50'
              )}
            >
              {opt.text}
            </button>
          ))}
        </div>
      </div>

      <Button onClick={submitAnswer} isDisabled={selectedOption === null || answering} className="w-full">
        {answering ? 'Submitting...' : 'Submit Answer'}
      </Button>
    </div>
  )
}
